/**
 * Brand Cart Drawer — Global Component
 * Handles displaying a single vendor's cart from anywhere in the site.
 * Provides consistent add-to-cart feedback and vendor-specific checkout.
 */

// Storefront-throttle aware fetch. Shopify's /cart.js, /cart/change.js and
// /cart/add.js all share a per-session rate limit; under bursty cart
// mutations (rapid +/-, cross-component fetches) the limit trips and the
// theme would silently fail otherwise. Two retries with backoff covers
// the typical recovery window without delaying real failures by much.
async function brandDrawerResilientFetch(url, init, retries) {
  if (typeof retries !== 'number') retries = 2;
  const backoffs = [1500, 3000];
  for (let attempt = 0; attempt <= retries; attempt++) {
    let response;
    try {
      response = await fetch(url, init);
    } catch (err) {
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, backoffs[attempt]));
        continue;
      }
      throw err;
    }
    if (response.status === 429 && attempt < retries) {
      await new Promise(r => setTimeout(r, backoffs[attempt]));
      continue;
    }
    return response;
  }
}

// BXGY freebie line-item property. Single source of truth is
// PROMO_CONFIG.freeFlag in bxgy-promo.js, surfaced cross-script via
// BxgyPromo.getFreeFlag(). The literal here is only the fallback for
// the script-load race (bxgy-promo.js not parsed yet) — it MUST match
// PROMO_CONFIG.freeFlag (and the hardcoded check in
// snippets/vendor-cart-card.liquid, which can't read JS config).
// Resolved at call time, not module load, so post-bootstrap values win.
function bxgyFreeFlag() {
  return (window.BxgyPromo && typeof window.BxgyPromo.getFreeFlag === 'function')
    ? window.BxgyPromo.getFreeFlag()
    : '_marini_q2_free';
}

// Slot keys of the active BXGY campaign ([] until bxgy-promo.js is
// ready). length < 2 means single-slot: no Serum/Cream-style mix to
// edit, so the drawer hides mix UI instead of wiring rebalance.
// With a line, the slots of the campaign that owns THAT line: several
// disjoint campaigns can run at once, each with its own slot count.
function bxgySlots(item) {
  if (!(window.BxgyPromo && window.BxgyPromo.isReady && window.BxgyPromo.isReady())) return [];
  if (item && typeof window.BxgyPromo.getSlotsForProduct === 'function') {
    return window.BxgyPromo.getSlotsForProduct(item.product_id);
  }
  return typeof window.BxgyPromo.getSlots === 'function' ? window.BxgyPromo.getSlots() : [];
}

// Does this cart line belong to an ACTIVE DISJOINT (Marini-style) bxgy
// runtime? Returns the mix slot ("serum"/"cream"/…) or null.
//
// v15 rule: the `_marini_q2_free` stamp alone no longer locks or
// rebalances a line. Pack campaigns (DBH cloths/toners/masks) never
// auto-add and never stamp new lines — the v15 checkout Function prices
// their packs from the cart contents and reads no flags. Old stamps in
// live carts are inert data. So a flagged line is "ours" only when an
// active disjoint runtime's productMap claims the product; everything
// else — legacy DBH-stamped lines, lines from expired campaigns, lines
// rendered before BxgyPromo finishes bootstrap — is a normal editable
// line. Locking those trapped the customer (a qty/remove control that
// never works is a support incident), while leaving them editable costs
// nothing: the Function never discounted them by the stamp anyway.
// (The drawer re-renders on bxgy:ready, so a Marini freebie line that
// was briefly editable during bootstrap locks as soon as the disjoint
// runtime resolves.)
function bxgyDisjointSlot(item) {
  if (!item || !item.properties || item.properties[bxgyFreeFlag()] !== '1') return null;
  if (!window.BxgyPromo || typeof window.BxgyPromo.isReady !== 'function' || !window.BxgyPromo.isReady()) return null;
  if (typeof window.BxgyPromo.getSlotForProduct !== 'function') return null;
  return window.BxgyPromo.getSlotForProduct(item.product_id);
}

// Copy for the note shown when bxgy-promo.js removed free gifts whose
// promotion ended (pruneEndedGiftLines). `gifts` is BxgyPromo.getEndedGifts
// for one vendor: [{ title, quantity }]. Returns null when there is nothing
// to say. Titles are product titles, so the copy is set with textContent.
//
// The copy says what is true in every case: the gift is "no longer part of
// an active promotion". "Ended" would be wrong for a paused campaign that
// resumes, a campaign seen in preview, or a gift swapped mid-campaign, and
// one Marini campaign with two gift products is still one promotion.
// Product titles cannot be pluralized ("3 free Hyla3D Serum"), so a count
// above one reads "units of".
function endedGiftNoticeCopy(gifts) {
  const list = (gifts || []).filter(Boolean);
  if (!list.length) return null;
  // One entry per product title: two removed lines of the same product
  // are one product to the clinic.
  const byTitle = [];
  list.forEach((g) => {
    const name = String(g.title || '').trim();
    const qty = Number(g.quantity) > 0 ? Number(g.quantity) : 1;
    const prior = byTitle.find(e => e.name === name);
    if (prior) prior.units += qty;
    else byTitle.push({ name, units: qty });
  });
  const units = byTitle.reduce((sum, e) => sum + e.units, 0);
  const names = byTitle.map(e => e.name || 'gift');
  let named;
  if (names.length === 1) named = names[0];
  else if (names.length === 2) named = names[0] + ' and ' + names[1];
  else named = names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];

  const title = units === 1 ? 'Free gift removed' : 'Free gifts removed';
  const plural = ' are no longer part of an active promotion, so we removed them from your cart rather than charge you for them.';
  let body;
  if (byTitle.length > 1) {
    body = 'Your free ' + named + plural;
  } else if (units > 1) {
    body = byTitle[0].name
      ? 'Your ' + units + ' free units of ' + named + plural
      : 'Your ' + units + ' free gifts' + plural;
  } else {
    body = 'Your free ' + named + ' is no longer part of an active promotion, so we removed it from your cart rather than charge you for it.';
  }
  return { title, body };
}

// The ended gifts whose stamped line is back in the cart: the promotion
// resumed after a pause (or preview and the live theme disagree), and the
// auto-add put the gift back. A note saying it was removed would sit right
// above its live Free line. Matched by line key, or by variant plus the
// stamp, since a gift added again may come back under another key.
// Only a cart read after the removal can show the gift back: one asked for
// before it still holds the very line the prune took out (the drawer was
// open, or its /cart.js was in flight, when the prune ran). `cartReadAt` is
// when that cart was requested; unknown proves nothing.
function endedGiftsBackInCart(gifts, cartItems, flag, cartReadAt) {
  if (typeof cartReadAt !== 'number') return [];
  const items = Array.isArray(cartItems) ? cartItems : [];
  return (gifts || []).filter(g => g && (typeof g.at !== 'number' || cartReadAt > g.at) && items.some(i => i && (
    (g.key != null && i.key === g.key) ||
    (g.variantId != null && String(i.variant_id) === String(g.variantId) &&
      !!i.properties && i.properties[flag] === '1')
  )));
}

// Fill and show the ended gift note, or hide it. Returns whether it shows.
function renderEndedGiftNotice(notice, gifts) {
  if (!notice) return false;
  const copy = endedGiftNoticeCopy(gifts);
  if (!copy) {
    notice.classList.add('hidden');
    return false;
  }
  const titleEl = notice.querySelector('[data-ended-notice-title]');
  const bodyEl = notice.querySelector('[data-ended-notice-body]');
  if (titleEl) titleEl.textContent = copy.title;
  if (bodyEl) bodyEl.textContent = copy.body;
  notice.classList.remove('hidden');
  return true;
}

class BrandCartDrawer {
  constructor() {
    this.config = window.brandCartDrawerConfig || {};
    this.cartData = null;
    // When this.cartData was requested (Date.now()), so the ended gift note
    // can tell a read made before a prune from one made after it.
    this.cartDataAt = null;
    this.currentVendor = null;
    this.isOpen = false;
    this.isUpdating = false;

    // Brands the customer has already purchased from (for opening order logic)
    this.purchasedBrands = new Set();

    // Cache DOM elements
    this.drawer = document.getElementById('brand-drawer');
    this.overlay = document.getElementById('brand-drawer-overlay');

    if (!this.drawer || !this.overlay) {
      console.warn('BrandCartDrawer: Required DOM elements not found');
      return;
    }

    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.bindGlobalEvents();
    this.fetchPurchasedBrands();
    this.restoreParkedItems();
    // bfcache back-navigation from checkout (the most common abandon path)
    // resumes the page without re-running init, so restore must fire there too.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.restoreParkedItems();
    });
  }

  /**
   * Fetch purchased brands for opening order logic
   */
  async fetchPurchasedBrands() {
    if (this.config.companyId) {
      await window.BrandOrderRequirements?.loadHistory();
      return;
    }
    if (!this.config.customerId) return;

    try {
      const response = await fetch(
        `${this.config.workerUrl}/customer/purchased-brands?customerId=${this.config.customerId}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.purchasedBrands) {
          data.purchasedBrands.forEach(brand => {
            this.purchasedBrands.add(brand.vendor);
          });
        }
      }
    } catch (error) {
      console.error('BrandCartDrawer: Error fetching purchased brands:', error);
    }
  }

  /**
   * Check if this is an opening order (first purchase with this brand)
   */
  isOpeningOrder(vendor) {
    return !this.purchasedBrands.has(vendor);
  }

  /**
   * Get the applicable minimum for a vendor based on whether it's an opening order.
   * Applies cart rules from config.brands[vendor].cartRules when the live cart
   * triggers them. See assets/cart-rule-engine.js for details.
   *
   * Returns { minimum: number (cents), isOpening: boolean, ruleName: string|null }
   */
  getMinimumForVendor(vendor) {
    const verified = window.BrandOrderRequirements?.evaluate(this.cartData, vendor);
    if (verified) return verified;
    const brandConfig = this.config.brands?.[vendor] || {};
    const baseOpening = brandConfig.openingMinimum ?? 0;
    const baseMinimum = brandConfig.minimum ?? this.config.defaultMinimum ?? 0;

    const rules = brandConfig.cartRules || [];
    const items = this.cartData?.items || [];

    let effMinimum = baseMinimum;
    let effOpening = baseOpening;
    let matchedRule = null;

    if (rules.length && window.CartRuleEngine) {
      const resolved = window.CartRuleEngine.resolveForVendor({
        rules,
        cartItems: items,
        vendor,
        baseMinimum,
        baseOpening,
      });
      effMinimum = resolved.minimum;
      effOpening = resolved.opening;
      matchedRule = resolved.matchedRule;
    }

    const isOpening = this.isOpeningOrder(vendor);
    if (isOpening && effOpening > 0) {
      return { minimum: effOpening * 100, isOpening: true, ruleName: matchedRule?.name || null };
    }
    return { minimum: effMinimum * 100, isOpening: false, ruleName: matchedRule?.name || null };
  }

  /**
   * Prime the CartRuleEngine tag cache for current cart items. Required before
   * calls to getMinimumForVendor() — rules compare against item tags.
   */
  async primeCartRuleTags() {
    const engine = window.CartRuleEngine;
    if (!engine || !this.cartData?.items?.length) return;
    try {
      await engine.primeTags(this.cartData.items);
    } catch (err) {
      console.warn('[BrandCartDrawer] primeCartRuleTags failed:', err);
    }
  }

  /**
   * Cache frequently accessed DOM elements
   */
  cacheElements() {
    this.elements = {
      // Header
      closeBtn: document.getElementById('brand-drawer-close'),
      icon: document.getElementById('brand-drawer-icon'),
      title: document.getElementById('brand-drawer-title'),
      subtotalHeader: document.getElementById('brand-drawer-subtotal-header'),
      originalHeader: document.getElementById('brand-drawer-original-header'),
      
      // Added banner
      addedBanner: document.getElementById('brand-drawer-added-banner'),
      addedText: document.getElementById('brand-drawer-added-text'),
      
      // Progress
      progress: document.getElementById('brand-drawer-progress'),
      progressText: document.getElementById('brand-drawer-progress-text'),
      progressFill: document.getElementById('brand-drawer-progress-fill'),
      progressCheck: document.getElementById('brand-drawer-progress-check'),
      
      // Badges
      badges: document.getElementById('brand-drawer-badges'),
      tierBadge: document.getElementById('brand-drawer-tier-badge'),
      
      // Body
      body: document.getElementById('brand-drawer-body'),
      items: document.getElementById('brand-drawer-items'),
      loading: document.getElementById('brand-drawer-loading'),
      
      // Footer
      originalPrice: document.getElementById('brand-drawer-original-price'),
      subtotal: document.getElementById('brand-drawer-subtotal'),
      tierSavings: document.getElementById('brand-drawer-tier-savings'),
      savingsAmount: document.getElementById('brand-drawer-savings-amount'),
      continueBtn: document.getElementById('brand-drawer-continue'),
      checkoutBtn: document.getElementById('brand-drawer-checkout'),
      viewAllLink: document.getElementById('brand-drawer-view-all'),
      
      // Template
      itemTemplate: document.getElementById('brand-drawer-item-template')
    };
  }

  /**
   * Bind drawer-specific events
   */
  bindEvents() {
    // Close button
    this.elements.closeBtn?.addEventListener('click', () => this.close());
    
    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Continue shopping
    this.elements.continueBtn?.addEventListener('click', () => this.close());
    
    // Checkout button
    this.elements.checkoutBtn?.addEventListener('click', () => this.handleCheckout());
  }

  /**
   * Bind global events for add-to-cart interception
   */
  bindGlobalEvents() {
    // Listen for cart updates from product forms
    document.addEventListener('cart:item-added', (e) => {
      const { vendor, productTitle } = e.detail || {};
      if (vendor) {
        this.openForVendor(vendor, productTitle);
      }
    });

    // Listen for pubsub cart updates
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        if (!this.isOpen || !this.currentVendor) return;
        // Skip our own publishes — we already populated post-mutation.
        if (event && event.source === 'brand-cart-drawer') return;
        // If the publisher included the post-mutation cart, render from
        // it directly instead of refetching. This eliminates a /cart.js
        // round-trip per cart-update — critical under storefront throttle
        // when multiple subscribers (OTP manager, product-info,
        // bxgy-promo) all listen for the same event.
        if (event && event.cartData && Array.isArray(event.cartData.items)) {
          const revision = this._cartRevision = (this._cartRevision || 0) + 1;
          const cart = event.cartData;
          this.cartData = cart;
          this.cartDataAt = Date.now();
          await this.primeCartRuleTags();
          if (revision !== this._cartRevision || this.cartData !== cart) return;
          await window.BrandOrderRequirements?.refresh(cart, { reuseCurrent: true });
          if (revision !== this._cartRevision || this.cartData !== cart || window.BrandOrderRequirements?.isCurrentCart?.(cart) === false) return;
          const vendorItems = this.cartData.items.filter(item => item.vendor === this.currentVendor);
          if (vendorItems.length === 0) {
            this.showEmptyState();
          } else {
            this.populateDrawer(this.currentVendor);
          }
          return;
        }
        this.refreshCart();
      });
    }

    // NOTE: no click handler for [data-action="view-cart"] here on purpose.
    // Those buttons exist only on the multi-vendor carts page, whose own
    // script binds them (with an internal-modal fallback) and calls
    // window.openBrandCartDrawer. A second, document-level handler here
    // made every click open the drawer twice (double fetch + dispatch).

    // Re-render freebie line steppers once bxgy-promo finishes bootstrap.
    // Without this, an open drawer that rendered BEFORE BxgyPromo was
    // ready stays in fallback-lock mode forever (syncFreeQty doesn't
    // pubsub when delta=0). One-shot listener: bootstrap fires once.
    window.addEventListener('bxgy:ready', () => {
      if (this.isOpen && this.currentVendor && this.cartData) {
        this.populateDrawer(this.currentVendor);
      }
    }, { once: true });

    // A gift whose promotion ended was just removed. The cart-update
    // publish that follows re-renders an open drawer anyway; this covers a
    // page without pubsub.
    window.addEventListener('bxgy:gifts-ended', () => {
      if (this.isOpen && this.currentVendor) this.updateEndedGiftNotice(this.currentVendor);
    });
  }

  /**
   * Open drawer for a specific vendor
   * @param {string} vendor - The vendor name
   * @param {string} addedProductTitle - Optional: title of just-added product
   */
  async openForVendor(vendor, addedProductTitle = null) {
    this.currentVendor = vendor;
    this.showLoading(true);
    this.open();

    // Show added banner if product was just added
    if (addedProductTitle) {
      this.showAddedBanner(addedProductTitle);
    } else {
      this.hideAddedBanner();
    }

    const cart = await this.fetchCartData();
    if (cart && this.currentVendor === vendor) this.populateDrawer(vendor);
    // Re-scope the promo card slots to this vendor + nudge the promo
    // modules to re-render. They listen for cart:updated and use
    // data-vendor on each [data-X-promo-card] to filter cart lines.
    this.scopePromoSlotsToVendor(vendor);
    this.showLoading(false);
  }

  /**
   * Update the data-vendor attribute on every promo card skeleton
   * inside the drawer so each promo module re-evaluates against the
   * vendor whose cart is currently displayed. Called whenever the
   * drawer transitions to a new vendor (via openForVendor).
   *
   * Why dispatch cart:updated rather than expose a custom event:
   * the promo modules already debounce-listen for cart:updated to
   * re-render banners; piggy-backing avoids a parallel pubsub channel
   * just for this side-effect-free re-scope.
   */
  scopePromoSlotsToVendor(vendor) {
    if (!this.drawer) return;
    var slots = this.drawer.querySelectorAll('[data-min-qty-promo-card], [data-bundle-promo-card]');
    slots.forEach(function (slot) {
      slot.setAttribute('data-vendor', vendor || '');
    });

    // BXGY keys off a HANDLE, not the raw vendor name (assignAndRenderCards
    // groups cart lines by handleize(item.vendor)), so it needs its own
    // stamping rather than riding the loop above.
    var handle = String(vendor || '')
      .toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    this.drawer.querySelectorAll('[data-bxgy-promo][data-bxgy-surface="drawer"]')
      .forEach(function (card) {
        if (card.getAttribute('data-vendor-handle') === handle) return;
        card.setAttribute('data-vendor-handle', handle);
        // Re-opening for a different brand must not leave the previous
        // brand's campaign assigned to this element.
        delete card.dataset.campaignId;
        card.dataset.state = 'loading';
        card.classList.add('hidden');
      });
    // A drawer OPEN is not a cart mutation, so the cart:updated below is the
    // only nudge BXGY would get — and it ignores the event's detail and reads
    // its own cache, which may predate this vendor stamp. Ask it explicitly.
    if (window.BxgyPromo && typeof window.BxgyPromo.refreshSurfaces === 'function') {
      window.BxgyPromo.refreshSurfaces();
    }
    // Tell promo modules to re-render against the new scope. The detail
    // must be the REAL cart (openForVendor fetched it just before this
    // call): listeners like multi-vendor-carts render straight from
    // detail, and a payload without `items` makes that page drop every
    // vendor card. Promo modules ignore detail and re-fetch on their own.
    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail: this.cartData }),
    );
  }

  /**
   * Open the drawer
   */
  open() {
    if (!this.drawer || !this.overlay) return;

    this.isOpen = true;
    window.brandCartDrawerOpen = true; // Flag to prevent old cart drawer from opening
    this.drawer.classList.add('open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Focus management
    setTimeout(() => {
      this.elements.closeBtn?.focus();
    }, 100);
  }

  /**
   * Close the drawer
   */
  close() {
    if (!this.drawer || !this.overlay) return;

    this.isOpen = false;
    window.brandCartDrawerOpen = false; // Reset flag
    // The ended gift note has been seen: say it once, not on every open.
    this.acknowledgeEndedGifts();
    this.drawer.classList.remove('open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('open');
    document.body.style.overflow = '';
    this.currentVendor = null;
    this.hideAddedBanner();
  }

  /**
   * Show loading state
   */
  showLoading(show) {
    if (this.elements.loading) {
      this.elements.loading.classList.toggle('hidden', !show);
    }
    if (this.elements.items) {
      this.elements.items.style.opacity = show ? '0.5' : '1';
    }
  }

  /**
   * Show the "item added" banner
   */
  showAddedBanner(productTitle) {
    if (this.elements.addedBanner && this.elements.addedText) {
      const text = productTitle 
        ? `"${productTitle}" added to cart` 
        : this.config.strings?.itemAdded || 'Item added to cart';
      this.elements.addedText.textContent = text;
      this.elements.addedBanner.classList.add('show');

      // Auto-hide after 4 seconds
      setTimeout(() => {
        this.hideAddedBanner();
      }, 4000);
    }
  }

  /**
   * Hide the "item added" banner
   */
  hideAddedBanner() {
    if (this.elements.addedBanner) {
      this.elements.addedBanner.classList.remove('show');
    }
  }

  /**
   * Fetch current cart data — retry-aware. Preserves last good cartData
   * on terminal failure so the drawer never blanks out under throttle.
   */
  async fetchCartData({ forceMinimums = false } = {}) {
    const revision = this._cartRevision = (this._cartRevision || 0) + 1;
    try {
      // Stamped before the request, not on arrival: an answer that lands
      // after a prune may still describe the cart from before it.
      const requestedAt = Date.now();
      const response = await brandDrawerResilientFetch(`${this.config.routes?.cartUrl || '/cart'}.js`);
      if (!response || !response.ok) {
        throw new Error(`fetchCartData ${response ? response.status : 'no-response'}`);
      }
      const cart = await response.json();
      if (revision !== this._cartRevision) return null;
      this.cartData = cart;
      this.cartDataAt = requestedAt;
      await this.primeCartRuleTags();
      if (revision !== this._cartRevision || this.cartData !== cart) return null;
      await window.BrandOrderRequirements?.refresh(cart, { forceHistory: forceMinimums });
      if (revision !== this._cartRevision || this.cartData !== cart || window.BrandOrderRequirements?.isCurrentCart?.(cart) === false) return null;
      return cart;
    } catch (error) {
      console.error('BrandCartDrawer: Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Refresh cart data and UI
   */
  async refreshCart() {
    if (!this.currentVendor || this.isUpdating) return;
    const cart = await this.fetchCartData();
    if (cart) this.populateDrawer(this.currentVendor);
  }

  /**
   * Populate the drawer with vendor's cart data
   */
  populateDrawer(vendor) {
    if (!this.cartData) return;

    const vendorItems = this.cartData.items.filter(item => item.vendor === vendor);
    const brandConfig = this.config.brands?.[vendor] || {};
    const requirement = this.getMinimumForVendor(vendor);
    const { minimum, isOpening } = requirement;
    const subtotal = vendorItems.reduce((sum, item) => sum + item.final_line_price, 0);
    const hasMinimum = minimum > 0;
    const progress = hasMinimum ? Math.min((subtotal / minimum) * 100, 100) : 100;
    const minimumReached = requirement.allowed !== false && (!hasMinimum || subtotal >= minimum);
    const amountToGo = Math.max(minimum - subtotal, 0);

    // Calculate tier based on cart subtotal
    const tierInfo = this.calculateTierFromSubtotal(vendor, subtotal);

    // Check if Shopify discounts are already applied for this vendor
    // If so, use original_line_price for strikethrough instead of recalculating tier %
    const hasShopifyDiscount = vendorItems.some(item =>
      (item.line_level_discount_allocations || []).some(d => d.amount > 0)
    );
    const originalSubtotal = hasShopifyDiscount
      ? vendorItems.reduce((sum, item) => sum + item.original_line_price, 0)
      : 0;

    // Update header (now includes tier pricing)
    this.updateHeader(vendor, subtotal, brandConfig, tierInfo, hasShopifyDiscount, originalSubtotal);

    // Update progress
    this.updateProgress(hasMinimum, minimumReached, minimum, amountToGo, progress, isOpening);
    window.BrandOrderRequirements?.render(this.elements.progress, requirement);
    if (requirement.messages?.length && this.elements.progress) {
      this.elements.progress.classList.remove('hidden');
      if (!hasMinimum && this.elements.progressText) this.elements.progressText.textContent = '';
      if (hasMinimum && subtotal >= minimum && this.elements.progressText) this.elements.progressText.textContent = 'Order amount reached';
    }
    this.elements.progress?.querySelector('.brand-drawer-progress-row')?.classList.toggle('hidden', !hasMinimum);

    // Update badges (tier info)
    this.updateBadges(tierInfo);

    // Populate items
    this.populateItems(vendorItems);

    // Mix notice — show when this vendor has BXGY freebies in cart
    this.updateMixNotice(vendorItems);

    // Winner note — show when the active BXGY campaign suppressed a
    // competing promo on this cart (PromoCompat arbitration)
    this.updateWinnerNote(vendorItems);

    // Ended gift note: a free gift of this vendor left the cart because
    // its promotion ended.
    this.updateEndedGiftNotice(vendor);

    // Update footer (now includes tier pricing)
    this.updateFooter(subtotal, minimumReached, tierInfo, hasShopifyDiscount, originalSubtotal);

    // Update tier progress bar
    this.updateTierProgress(subtotal, tierInfo);

    // Check if no items (cart was emptied)
    if (vendorItems.length === 0) {
      this.showEmptyState();
    }
  }

  /**
   * Calculate tier based on cart subtotal
   */
  calculateTierFromSubtotal(vendor, subtotal) {
    // Try multiple config sources for brand tiers (drawer config, or multi-vendor config)
    const brandTiersConfig = this.config.brandSpendTiers?.[vendor] 
      || window.multiVendorCartsConfig?.brandSpendTiers?.[vendor];
    
    if (!brandTiersConfig || !brandTiersConfig.enabled || !brandTiersConfig.tiers?.length) {
      return null;
    }

    const tiers = brandTiersConfig.tiers;
    
    // Sort tiers by minSpend descending
    const sortedTiers = [...tiers].sort((a, b) => b.minSpend - a.minSpend);
    
    // Subtotal in dollars
    const subtotalDollars = subtotal / 100;
    
    // Find current tier
    let currentTier = null;
    for (const tier of sortedTiers) {
      if (subtotalDollars >= tier.minSpend) {
        currentTier = {
          name: tier.name,
          threshold: tier.minSpend,
          discount: tier.discount
        };
        break;
      }
    }
    
    // Find next tier
    let nextTier = null;
    const sortedAscending = [...tiers].sort((a, b) => a.minSpend - b.minSpend);
    for (const tier of sortedAscending) {
      if (subtotalDollars < tier.minSpend) {
        nextTier = {
          name: tier.name,
          threshold: tier.minSpend,
          discount: tier.discount
        };
        break;
      }
    }

    return {
      currentTier,
      nextTier,
      subtotalDollars
    };
  }

  /**
   * Clean tier name by removing brand prefix
   */
  cleanTierName(tierName) {
    if (!tierName) return '';
    // Remove patterns like "Brand - ", "Brand: ", "Brand– "
    return tierName.replace(/^[^-:–]+[-:–]\s*/i, '').trim();
  }

  /**
   * Get tier color based on tier name
   */
  getTierColor(tierName) {
    // Use neutral theme-consistent color for all tiers
    return 'var(--bcd-text, #1a1a1a)';
  }

  /**
   * Update drawer header
   */
  updateHeader(vendor, subtotal, brandConfig, tierInfo, hasShopifyDiscount = false, originalSubtotal = 0) {
    // Keep the brand readable even when its supplied logo is a small wordmark.
    if (this.elements.title) {
      this.elements.title.textContent = vendor;
      this.elements.title.classList.remove('hidden');
    }
    if (this.elements.icon) {
      this.elements.icon.replaceChildren();
      this.elements.icon.classList.toggle('hidden', !brandConfig.icon);
      if (brandConfig.icon) {
        const logo = document.createElement('img');
        logo.src = brandConfig.icon;
        logo.alt = '';
        logo.width = 40;
        logo.height = 40;
        this.elements.icon.appendChild(logo);
      }
    }

    // Subtotal header with tier pricing
    this.updateHeaderPricing(subtotal, tierInfo, hasShopifyDiscount, originalSubtotal);
  }

  /**
   * Update header pricing (original + discounted)
   * When hasShopifyDiscount is true, show Shopify's original_line_price as strikethrough
   * and final_line_price as the current price (already discounted by Shopify).
   * When no Shopify discount, calculate tier % visually as before.
   */
  updateHeaderPricing(subtotal, tierInfo, hasShopifyDiscount = false, originalSubtotal = 0) {
    const hasTier = tierInfo?.currentTier;
    const discount = hasTier ? tierInfo.currentTier.discount : 0;

    let showStrikethrough = false;
    let strikethroughAmount = 0;
    let displaySubtotal = subtotal;

    if (hasShopifyDiscount && originalSubtotal > subtotal) {
      // Shopify discount: show original price strikethrough, final price as current
      showStrikethrough = true;
      strikethroughAmount = originalSubtotal;
      displaySubtotal = subtotal;
    } else if (hasTier && discount > 0) {
      // No Shopify discount: calculate tier % visually
      showStrikethrough = true;
      strikethroughAmount = subtotal;
      displaySubtotal = Math.round(subtotal * (1 - discount / 100));
    }

    // Original price (strikethrough)
    if (this.elements.originalHeader) {
      if (showStrikethrough) {
        this.elements.originalHeader.textContent = 'WSP ' + this.formatMoney(strikethroughAmount);
        this.elements.originalHeader.classList.remove('hidden');
      } else {
        this.elements.originalHeader.classList.add('hidden');
      }
    }

    // Current/discounted price
    if (this.elements.subtotalHeader) {
      this.elements.subtotalHeader.innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(displaySubtotal);
    }
  }

  /**
   * Update progress bar
   */
  updateProgress(hasMinimum, minimumReached, minimum, amountToGo, progress, isOpening = false) {
    if (!this.elements.progress) return;

    if (!hasMinimum) {
      this.elements.progress.classList.add('hidden');
      return;
    }

    this.elements.progress.classList.remove('hidden');
    const minimumLabel = isOpening ? 'Opening order' : 'Minimum order';

    // Progress text
    if (this.elements.progressText) {
      if (minimumReached) {
        this.elements.progressText.innerHTML = `<span class="progress-complete">${this.config.strings?.minimumReached || 'Minimum reached'}</span>`;
      } else {
        this.elements.progressText.textContent = `${minimumLabel}: ${this.formatMoney(minimum)} • ${this.formatMoney(amountToGo)} ${this.config.strings?.toGo || 'to go'}`;
      }
    }

    // Progress fill
    if (this.elements.progressFill) {
      this.elements.progressFill.style.width = `${progress}%`;
      this.elements.progressFill.classList.toggle('complete', minimumReached);
    }

    // Progress check icon
    if (this.elements.progressCheck) {
      this.elements.progressCheck.classList.toggle('complete', minimumReached);
    }
  }

  /**
   * Update badges (tier)
   */
  updateBadges(tierInfo) {
    const hasTier = tierInfo?.currentTier;

    if (this.elements.badges) {
      this.elements.badges.classList.toggle('hidden', !hasTier);
    }

    // Tier badge
    if (this.elements.tierBadge) {
      if (hasTier) {
        const cleanName = this.cleanTierName(tierInfo.currentTier.name);
        const discount = tierInfo.currentTier.discount;

        this.elements.tierBadge.innerHTML = `${discount}% OFF`;
        this.elements.tierBadge.title = `${cleanName} tier discount`;
        this.elements.tierBadge.classList.remove('hidden');
      } else {
        this.elements.tierBadge.classList.add('hidden');
      }
    }
  }

  /**
   * Toggle the BXGY mix-split notice in the drawer header. The drawer is
   * brand-scoped, so we only consider this vendor's items when deciding
   * whether the notice applies. The notice itself just routes the user
   * to /pages/carts where the mix editor lives — drawer doesn't surface
   * the editor because the freebies render as locked line items here.
   */
  updateMixNotice(vendorItems) {
    const notice = document.getElementById('brand-drawer-mix-notice');
    if (!notice) return;
    // "Ours" = lines of an ACTIVE disjoint runtime only (see
    // bxgyDisjointSlot): legacy pack-campaign stamps are inert and
    // must not bring up the Serum/Cream mix notice.
    // Single-slot campaigns have no split to adjust — the notice's
    // whole point is the Serum/Cream mix steppers, so hide it. Decided
    // per line: only a freebie whose OWN campaign has a mix counts.
    const hasMixableFree = vendorItems.some(item => {
      if (bxgyDisjointSlot(item) === null) return false;
      const slots = bxgySlots(item);
      return !(slots.length > 0 && slots.length < 2);
    });
    if (hasMixableFree) notice.classList.remove('hidden');
    else notice.classList.add('hidden');
  }

  /**
   * Toggle the "Your biggest saving is applied. Offers can't be
   * combined." note (#brand-drawer-winner-note). Drawer-level note is
   * ONLY for the BXGY winner — bxgy has no drawer promo-card, while
   * min-qty / bundle winners surface the same note inside their own
   * promo-card slots above. Shows when:
   *   - this vendor's cart has BXGY freebie lines (= the promo is
   *     actually firing on a qualified cart), AND
   *   - PromoCompat arbitration SUPPRESSED a competing promo in this
   *     campaign's favor (suppressedBy non-empty — implies the winner
   *     projected strictly-positive savings).
   * Fail-CLOSED: missing BxgyPromo/PromoCompat (older deploys, script
   * error) or no suppressed competitor → stays hidden. With a single
   * live promo nothing new ever renders.
   */
  updateWinnerNote(vendorItems) {
    const note = document.getElementById('brand-drawer-winner-note');
    if (!note) return;
    let show = false;
    try {
      // Active disjoint runtime lines only — pack campaigns (DBH) never
      // stamp lines under v15, and old stamps are inert data that must
      // not light up the winner note on their own.
      const hasPromoFree = vendorItems.some(item => bxgyDisjointSlot(item) !== null);
      if (
        hasPromoFree &&
        window.BxgyPromo && window.BxgyPromo.isReady && window.BxgyPromo.isReady() &&
        typeof window.BxgyPromo.getCampaignId === 'function' &&
        window.PromoCompat && typeof window.PromoCompat.suppressedBy === 'function'
      ) {
        const campaignId = window.BxgyPromo.getCampaignId();
        const beaten = campaignId != null
          ? (window.PromoCompat.suppressedBy(campaignId) || [])
          : [];
        show = beaten.length > 0;
      }
    } catch (e) {
      show = false;
    }
    note.classList.toggle('hidden', !show);
  }

  /**
   * Show the "Free gift removed" note when bxgy-promo.js took a gift of
   * this vendor out of the cart because its promotion ended. The list
   * lives in BxgyPromo (per tab), so the note still appears when the
   * removal happened on an earlier page. A gift whose stamped line is
   * back in the cart is left out (and forgotten), and the note of the
   * brand the drawer is switching away from counts as seen.
   */
  updateEndedGiftNotice(vendor) {
    const notice = document.getElementById('brand-drawer-ended-notice');
    if (!notice) return;
    // Switching brands with the drawer open: the note on screen was seen,
    // so it must not come back the next time that brand's drawer opens.
    if (this.endedNoticeVendor && this.endedNoticeVendor !== vendor) this.acknowledgeEndedGifts();
    const api = window.BxgyPromo;
    let gifts = [];
    try {
      if (vendor && api && typeof api.getEndedGifts === 'function') gifts = api.getEndedGifts(vendor);
    } catch (e) {
      gifts = [];
    }
    // A gift that is back in the cart was not removed after all: drop it
    // from the note, and forget it so the note cannot resurface when that
    // live gift later leaves for an ordinary reason.
    const back = endedGiftsBackInCart(gifts, this.cartData && this.cartData.items, bxgyFreeFlag(), this.cartDataAt);
    if (back.length) {
      gifts = gifts.filter(g => back.indexOf(g) === -1);
      try {
        if (typeof api.forgetEndedGifts === 'function') api.forgetEndedGifts(back.map(g => g.key));
      } catch (e) {
        /* the filter above still keeps the note off this render */
      }
    }
    this.endedNoticeVendor = renderEndedGiftNotice(notice, gifts) ? vendor : null;

    if (notice.dataset.dismissWired === '1') return;
    notice.dataset.dismissWired = '1';
    const dismiss = notice.querySelector('[data-ended-notice-dismiss]');
    if (!dismiss) return;
    dismiss.addEventListener('click', () => {
      this.acknowledgeEndedGifts();
      notice.classList.add('hidden');
      // The button just disappeared under the focus: hand it to the drawer.
      this.elements.closeBtn?.focus();
    });
  }

  /** Forget the ended gifts the note is showing (dismissed, or seen and closed). */
  acknowledgeEndedGifts() {
    const vendor = this.endedNoticeVendor;
    this.endedNoticeVendor = null;
    const api = window.BxgyPromo;
    if (!vendor || !api || typeof api.dismissEndedGifts !== 'function') return;
    try {
      api.dismissEndedGifts(vendor);
    } catch (e) {
      /* nothing to undo: the note simply shows again next time */
    }
  }

  /**
   * Populate cart items
   */
  populateItems(vendorItems) {
    if (!this.elements.items || !this.elements.itemTemplate) return;

    this.elements.items.innerHTML = '';

    vendorItems.forEach(item => {
      const itemEl = this.elements.itemTemplate.content.cloneNode(true);
      const container = itemEl.querySelector('.brand-drawer-item');

      container.dataset.lineKey = item.key;
      container.dataset.variantId = item.variant_id;

      // Promo-free flag: lines added by bxgy-promo.js for an ACTIVE
      // disjoint (Marini) runtime carry the PROMO_CONFIG.freeFlag
      // property AND resolve a mix slot — see bxgyDisjointSlot. They
      // render as locked/system-managed since the qty is auto-balanced
      // against the qualifying count by syncFreeQty() — manual edits
      // would just be re-added. The mix editor on /pages/carts is the
      // editing surface. Lines stamped by pack campaigns or expired
      // promos are inert: normal styling, normal controls.
      const isPromoFree = bxgyDisjointSlot(item) !== null;
      if (isPromoFree) container.classList.add('brand-drawer-item--promo-free');

      // Image
      const img = itemEl.querySelector('img');
      const imgLink = itemEl.querySelector('.brand-drawer-item-image-link');
      if (item.image) {
        img.src = this.getSizedImageUrl(item.image, '160x160');
        img.alt = item.title;
      } else {
        img.parentElement.innerHTML = `<div class="brand-drawer-item-placeholder">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
            <circle cx="9" cy="9" r="2"></circle>
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
          </svg>
        </div>`;
      }
      if (imgLink) imgLink.href = item.url;

      // Title and details
      const titleEl = itemEl.querySelector('.brand-drawer-item-title');
      titleEl.textContent = item.product_title;
      titleEl.href = item.url;

      itemEl.querySelector('.brand-drawer-item-variant').textContent = item.variant_title || '';
      itemEl.querySelector('.brand-drawer-item-price').innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(item.price);

      // Check if this is a one-time purchase product
      const isOneTimePurchase = item.properties && item.properties._one_time_purchase === 'true';

      // Force quantity to 1 for one-time purchase products
      if (isOneTimePurchase && item.quantity > 1) {
        // Update quantity to 1
        this.updateItemQuantity(item.key, 1);
        itemEl.querySelector('.brand-drawer-qty-input').value = 1;
      } else {
        itemEl.querySelector('.brand-drawer-qty-input').value = item.quantity;
      }

      // Per-line pricing.
      //
      // Shopify exposes two relevant fields on /cart.js items:
      //   - original_line_price: pre-discount line total (price × qty)
      //   - final_line_price:    post-discount line total
      //
      // When a Shopify discount has fired (automatic or app-Function),
      // the gap is non-zero — surface the original strikethrough so
      // practitioners SEE the saving in the drawer rather than only at
      // checkout. This is what was missing per Manuel's feedback ("no
      // se ven los descuentos … precio anterior tachado").
      var linePriceEl = itemEl.querySelector('.brand-drawer-item-line-price');
      if (linePriceEl) {
        var originalLinePrice = item.original_line_price;
        var finalLinePrice = item.final_line_price;
        var hasLineDiscount =
          typeof originalLinePrice === 'number' &&
          typeof finalLinePrice === 'number' &&
          originalLinePrice > finalLinePrice;
        if (hasLineDiscount) {
          // Strikethrough WSP + final WSP + tiny savings tag.
          var savedAmount = originalLinePrice - finalLinePrice;
          var pct = Math.round((savedAmount / originalLinePrice) * 100);
          linePriceEl.innerHTML =
            '<span class="brand-drawer-item-line-price-original">' +
              '<span class="brand-drawer-wsp-label">WSP</span> ' +
              this.formatMoney(originalLinePrice) +
            '</span>' +
            '<span class="brand-drawer-item-line-price-final">' +
              '<span class="brand-drawer-wsp-label">WSP</span> ' +
              this.formatMoney(finalLinePrice) +
            '</span>' +
            '<span class="brand-drawer-item-line-price-saving">−' + pct + '%</span>';
          itemEl.querySelector('.brand-drawer-item-line-total').classList.add('brand-drawer-item-line-total--discounted');
        } else {
          linePriceEl.innerHTML =
            '<span class="brand-drawer-wsp-label">WSP</span> ' +
            this.formatMoney(finalLinePrice);
        }
      }

      // Bind quantity controls
      this.bindItemControls(itemEl, item);

      this.elements.items.appendChild(itemEl);
    });
  }

  /**
   * Bind quantity and remove controls for an item
   */
  bindItemControls(itemEl, item) {
    const decreaseBtn = itemEl.querySelector('[data-action="decrease"]');
    const increaseBtn = itemEl.querySelector('[data-action="increase"]');
    const removeBtn = itemEl.querySelector('[data-action="remove"]');
    const qtyInput = itemEl.querySelector('.brand-drawer-qty-input');

    // Promo-free lines (bxgy 1:1 freebies of an ACTIVE DISJOINT
    // runtime) — qty +/− rebalance the Serum/Cream split via
    // BxgyPromo.rebalance(). The total stays equal to the qualifying
    // SCMS count (Shopify's Function enforces the discount cap
    // regardless), so each click is a delta-pair under the hood: −1
    // from one slot, +1 to the other. The remove button stays
    // disabled — removing a freebie line entirely would break the
    // entitlement and the auto-add would just re-create the line.
    //
    // ⛔ v15: bxgyDisjointSlot returns null for any line no active
    // disjoint runtime claims — legacy `_marini_q2_free` lines from DBH
    // pack campaigns, lines stamped by expired promos, and lines
    // rendered before BxgyPromo is ready. Those fall THROUGH to the
    // normal controls below: they are ordinary paid lines and their
    // qty/remove must keep working (the old flag-only lock trapped
    // exactly those customers).
    //
    // The qty input is read-only (typing 8 into a 6-entitlement total
    // doesn't have a sensible delta-pair interpretation). Customer
    // adjusts the split via the +/− buttons only.
    const disjointSlot = bxgyDisjointSlot(item);
    if (disjointSlot) {
      const slot = disjointSlot;
      // Single-slot campaign: there is no "other slot" to trade with,
      // so the +/− mix steppers are meaningless. Hide them entirely
      // (qty stays visible, read-only — it's auto-balanced against the
      // qualifying count) and keep remove disabled like other freebies.
      const slots = bxgySlots(item);
      if (slots.length < 2) {
        [decreaseBtn, increaseBtn].forEach(el => {
          if (el) el.style.display = 'none';
        });
        if (qtyInput) {
          qtyInput.readOnly = true;
          qtyInput.style.cursor = 'default';
        }
        if (removeBtn) {
          removeBtn.disabled = true;
          removeBtn.style.opacity = '0.4';
          removeBtn.style.cursor = 'not-allowed';
          removeBtn.title = 'Free with your qualifying purchase — quantity adjusts automatically.';
        }
        return;
      }
      // Mark the line so CSS can style freebie steppers distinctly if
      // we ever want to (e.g. accent color). Pure metadata for now.
      const itemContainer = itemEl.querySelector('.brand-drawer-item');
      if (itemContainer) {
        itemContainer.dataset.bxgySlot = slot;
        itemContainer.classList.add('brand-drawer-item--bxgy-mix');
      }
      // Qty input read-only — typing breaks the delta-pair semantics.
      if (qtyInput) {
        qtyInput.readOnly = true;
        qtyInput.style.cursor = 'default';
      }
      // Remove stays disabled (removing destroys the freebie line —
      // auto-add would just re-add it on the next cart event).
      if (removeBtn) {
        removeBtn.disabled = true;
        removeBtn.style.opacity = '0.4';
        removeBtn.style.cursor = 'not-allowed';
        removeBtn.title = 'Free with your SCMS — adjust the split with +/− instead.';
      }
      // Bind the +/− to BxgyPromo.rebalance.
      // Optimistic UI: update qty input + sibling line's input, disable
      // buttons until the promise settles.
      const callRebalance = (sign) => {
        if (decreaseBtn?.disabled || increaseBtn?.disabled) return;
        const owned = window.BxgyPromo.getOwned(slot);
        // Bail if the move would push the slot outside [0, total].
        if (sign === -1 && owned[slot] < 1) return;
        if (sign === 1) {
          // "The other slot" — generic over the campaign's slot keys
          // (Q2: serum↔cream). Single-slot lines never reach here
          // (steppers hidden above).
          const otherSlot = slots.filter(s => s !== slot)[0];
          if (!otherSlot || owned[otherSlot] < 1) return;
        }
        // Optimistic stepper value for THIS line.
        if (qtyInput) qtyInput.value = String(owned[slot] + sign);
        // Lock both freebie lines' steppers across the drawer until
        // the rebalance completes (prevents racing clicks).
        const allFreebieBtns = this.elements.items.querySelectorAll(
          '.brand-drawer-item--bxgy-mix .brand-drawer-qty-btn'
        );
        allFreebieBtns.forEach(b => {
          b.disabled = true;
          b.style.opacity = '0.4';
        });
        window.BxgyPromo.rebalance(slot, sign).catch(err => {
          console.warn('[brand-cart-drawer] rebalance failed:', err);
          // The drawer will re-render from cart:updated (rebalance
          // pubsubs on success, our own listener handles failure
          // rollback by re-fetching cart). If render doesn't happen
          // within 1s, force a refetch + repopulate.
          setTimeout(() => {
            allFreebieBtns.forEach(b => {
              b.disabled = false;
              b.style.opacity = '';
            });
          }, 1000);
        });
      };
      decreaseBtn?.addEventListener('click', () => callRebalance(-1));
      increaseBtn?.addEventListener('click', () => callRebalance(1));
      return;
    }

    // Check if this is a one-time purchase product
    const isOneTimePurchase = item.properties && item.properties._one_time_purchase === 'true';

    if (isOneTimePurchase) {
      // Disable quantity controls for one-time purchase products
      if (decreaseBtn) {
        decreaseBtn.disabled = true;
        decreaseBtn.style.opacity = '0.5';
        decreaseBtn.style.cursor = 'not-allowed';
      }
      if (increaseBtn) {
        increaseBtn.disabled = true;
        increaseBtn.style.opacity = '0.5';
        increaseBtn.style.cursor = 'not-allowed';
      }
      if (qtyInput) {
        qtyInput.disabled = true;
        qtyInput.style.opacity = '0.7';
        qtyInput.style.cursor = 'not-allowed';
      }

      // Keep remove button enabled
      removeBtn?.addEventListener('click', () => {
        this.updateItemQuantity(item.key, 0);
      });

      return; // Don't bind the quantity change listeners
    }

    // Normal behavior for regular products — disable decrease when at MOQ.
    // Remove button stays enabled (MOQ allows qty=0 removal).
    const moq = (window.__moqGuard && window.__moqGuard.getMOQ)
      ? window.__moqGuard.getMOQ(item.variant_id)
      : 1;
    const atMoq = item.quantity <= moq && moq > 1;
    if (decreaseBtn) {
      if (atMoq) {
        decreaseBtn.disabled = true;
        decreaseBtn.style.opacity = '0.4';
        decreaseBtn.style.cursor = 'not-allowed';
        decreaseBtn.title = `Minimum order quantity is ${moq}. Use the trash icon to remove.`;
      } else {
        decreaseBtn.disabled = false;
        decreaseBtn.style.opacity = '';
        decreaseBtn.style.cursor = '';
        decreaseBtn.removeAttribute('title');
      }
    }
    if (qtyInput) qtyInput.min = moq > 1 ? moq : 0;

    decreaseBtn?.addEventListener('click', () => {
      if (decreaseBtn.disabled) return;
      this.updateItemQuantity(item.key, Math.max(0, item.quantity - 1));
    });

    increaseBtn?.addEventListener('click', () => {
      this.updateItemQuantity(item.key, item.quantity + 1);
    });

    removeBtn?.addEventListener('click', () => {
      this.updateItemQuantity(item.key, 0);
    });

    qtyInput?.addEventListener('change', (e) => {
      const newQty = parseInt(e.target.value) || 0;
      this.updateItemQuantity(item.key, newQty);
    });
  }

  /**
   * Update item quantity
   */
  async updateItemQuantity(lineKey, quantity) {
    if (this.isUpdating) return;
    const revision = this._cartRevision = (this._cartRevision || 0) + 1;
    this.isUpdating = true;
    this.showLoading(true);

    try {
      const requestedAt = Date.now();
      const response = await brandDrawerResilientFetch(
        `${this.config.routes?.cartChangeUrl || '/cart/change'}.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: quantity
          })
        }
      );

      if (!response || !response.ok) {
        throw new Error(`HTTP error! status: ${response ? response.status : 'no-response'}`);
      }

      const cart = await response.json();
      if (revision !== this._cartRevision) return;
      this.cartData = cart;
      this.cartDataAt = requestedAt;
      await this.primeCartRuleTags();
      if (revision !== this._cartRevision || this.cartData !== cart) return;
      await window.BrandOrderRequirements?.refresh(cart);
      if (revision !== this._cartRevision || this.cartData !== cart || window.BrandOrderRequirements?.isCurrentCart?.(cart) === false) return;

      // Check if vendor still has items
      const vendorItems = this.cartData.items.filter(item => item.vendor === this.currentVendor);
      if (vendorItems.length === 0) {
        this.showEmptyState();
      } else {
        this.populateDrawer(this.currentVendor);
      }

      // Update cart icon bubble
      this.updateCartBubble();

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: this.cartData }));

      // Also publish to pubsub if available
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-drawer', cartData: this.cartData });
      }

    } catch (error) {
      console.error('BrandCartDrawer: Error updating quantity:', error);
    } finally {
      this.isUpdating = false;
      this.showLoading(false);
    }
  }

  /**
   * Update footer with totals
   * When hasShopifyDiscount is true, show Shopify's original_line_price as strikethrough.
   * When no Shopify discount, calculate tier % visually as before.
   */
  updateFooter(subtotal, minimumReached, tierInfo, hasShopifyDiscount = false, originalSubtotal = 0) {
    const hasTier = tierInfo?.currentTier;
    const discount = hasTier ? tierInfo.currentTier.discount : 0;

    let showStrikethrough = false;
    let strikethroughAmount = 0;
    let displaySubtotal = subtotal;
    let savingsAmount = 0;

    if (hasShopifyDiscount && originalSubtotal > subtotal) {
      // Shopify discount: show original price strikethrough, final price as current
      showStrikethrough = true;
      strikethroughAmount = originalSubtotal;
      displaySubtotal = subtotal;
      savingsAmount = originalSubtotal - subtotal;
    } else if (hasTier && discount > 0) {
      // No Shopify discount: calculate tier % visually
      showStrikethrough = true;
      strikethroughAmount = subtotal;
      displaySubtotal = Math.round(subtotal * (1 - discount / 100));
      savingsAmount = subtotal - displaySubtotal;
    }

    // Show original price strikethrough
    if (this.elements.originalPrice) {
      if (showStrikethrough) {
        this.elements.originalPrice.textContent = 'WSP ' + this.formatMoney(strikethroughAmount);
        this.elements.originalPrice.classList.remove('hidden');
      } else {
        this.elements.originalPrice.classList.add('hidden');
      }
    }

    // Subtotal
    if (this.elements.subtotal) {
      this.elements.subtotal.innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(displaySubtotal);
    }

    // Tier savings section
    if (this.elements.tierSavings) {
      if (showStrikethrough && savingsAmount > 0) {
        this.elements.tierSavings.classList.remove('hidden');
        if (this.elements.savingsAmount) {
          this.elements.savingsAmount.textContent = this.formatMoney(savingsAmount);
        }
      } else {
        this.elements.tierSavings.classList.add('hidden');
      }
    }

    // Checkout button
    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.disabled = !minimumReached;
    }
  }

  /**
   * Update tier progress bar in drawer
   */
  updateTierProgress(subtotal, tierInfo) {
    const progressContainer = document.getElementById('brand-drawer-tier-progress');
    
    if (!progressContainer) return;

    // If no tier data or at max tier, hide the progress
    if (!tierInfo || !tierInfo.nextTier) {
      progressContainer.classList.add('hidden');
      return;
    }

    const { nextTier, subtotalDollars } = tierInfo;
    const amountNeeded = nextTier.threshold - subtotalDollars;
    const cleanName = this.cleanTierName(nextTier.name);
    const color = this.getTierColor(nextTier.name);

    // Show the progress
    progressContainer.classList.remove('hidden');
    progressContainer.innerHTML = `
      <div class="brand-drawer-tier-progress-content">
        <div class="tier-progress-text">
          Spend <strong>${this.formatMoney(amountNeeded * 100)}</strong> more for 
          <strong>${cleanName}</strong> (${nextTier.discount}% off)
        </div>
        <div class="tier-progress-bar">
          <div class="tier-progress-fill" style="width: ${Math.min((subtotalDollars / nextTier.threshold) * 100, 100)}%"></div>
        </div>
      </div>
    `;
  }

  /**
   * Show empty state when no items
   */
  showEmptyState() {
    if (this.elements.items) {
      this.elements.items.innerHTML = `
        <div class="brand-drawer-empty">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="8" cy="21" r="1"></circle>
            <circle cx="19" cy="21" r="1"></circle>
            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
          </svg>
          <p>No items from this brand in your cart</p>
        </div>
      `;
    }

    // Disable checkout
    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.disabled = true;
    }

    // Hide progress
    if (this.elements.progress) {
      this.elements.progress.classList.add('hidden');
    }

    // Update header subtotal
    if (this.elements.subtotalHeader) {
      this.elements.subtotalHeader.innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(0);
    }
    if (this.elements.originalHeader) {
      this.elements.originalHeader.classList.add('hidden');
    }
    if (this.elements.subtotal) {
      this.elements.subtotal.innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(0);
    }

    // Hide tier elements
    if (this.elements.tierBadge) {
      this.elements.tierBadge.classList.add('hidden');
    }
    if (this.elements.badges) {
      this.elements.badges.classList.add('hidden');
    }
    if (this.elements.originalPrice) {
      this.elements.originalPrice.classList.add('hidden');
    }
    if (this.elements.tierSavings) {
      this.elements.tierSavings.classList.add('hidden');
    }
    const tierProgress = document.getElementById('brand-drawer-tier-progress');
    if (tierProgress) {
      tierProgress.classList.add('hidden');
    }
  }

  /**
   * Handle checkout for current vendor
   */
  async handleCheckout() {
    // Ignore re-entrant clicks while a checkout attempt is in flight — the
    // pre-checkout re-sync makes the click briefly async, and a double-click
    // would interleave two isolation runs against the same shared cart.
    if (this._checkoutInFlight) return;
    this._checkoutInFlight = true;
    try {
      await this._handleCheckoutInner();
    } finally {
      this._checkoutInFlight = false;
    }
  }

  async _handleCheckoutInner() {
    if (!this.currentVendor || !this.cartData) return;

    // A parked-items restore may still be re-adding another brand's items —
    // let it settle first, otherwise its /cart/add could land inside the
    // checkout-redirect window below and re-mix a verified single-brand
    // checkout (the fresh snapshot would never have seen those items).
    if (window.__parkedRestoreInFlight) {
      try { await window.__parkedRestoreInFlight; } catch (e) { /* restore handles its own errors */ }
    }

    // Snapshot the vendor: closing the drawer mid-flow (Escape/overlay/close
    // button) nulls this.currentVendor, and everything after the awaits below must
    // keep operating on the vendor the buyer actually clicked Checkout for.
    const vendor = this.currentVendor;

    // Re-sync with the authoritative server cart before deciding what to isolate
    // (guards against a stale this.cartData from another tab/device that would skip
    // the isolation+verify block below and leak a mixed cart to checkout).
    // Fail closed: without a fresh, server-confirmed cart we never proceed.
    const freshCart = await this.fetchCartData({ forceMinimums: true });
    if (!freshCart || !Array.isArray(freshCart.items)) {
      alert('Sorry, we could not prepare your single-brand checkout. Please try again.');
      return;
    }

    const vendorItems = freshCart.items.filter(item => item.vendor === vendor);
    const otherItems = freshCart.items.filter(item => item.vendor !== vendor);

    if (vendorItems.length === 0) return;

    // Check minimum
    const requirement = this.getMinimumForVendor(vendor);
    const { minimum, isOpening } = requirement;
    const subtotal = vendorItems.reduce((sum, item) => sum + item.final_line_price, 0);

    if (minimum > 0 && subtotal < minimum) {
      const minType = isOpening ? 'opening order' : 'minimum order';
      alert(`Please add ${this.formatMoney(minimum - subtotal)} more to meet the ${minType} for ${vendor}.`);
      return;
    }

    if (requirement.allowed === false) {
      alert(requirement.messages.join(' '));
      return;
    }

    // Per-item MOQ check — block checkout if any line item is below variant's custom.moq
    const moqGet = (window.__moqGuard && window.__moqGuard.getMOQ) || ((id) => (window.__MOQ_MAP__ && window.__MOQ_MAP__[id]) || 1);
    const moqViolations = vendorItems
      .map((item) => ({ item, moq: requirement.exempt ? 1 : moqGet(item.variant_id) }))
      .filter(({ item, moq }) => item.quantity < moq);
    if (moqViolations.length > 0) {
      const lines = moqViolations.map(({ item, moq }) => `\u2022 ${item.product_title}: ${item.quantity} (need ${moq})`).join('\n');
      alert(`Cannot checkout ${vendor}. Minimum order quantity not met:\n\n${lines}`);
      return;
    }

    // If there are other items, remove them and VERIFY the cart is now
    // single-vendor BEFORE redirecting. Fail closed: never send a mixed cart to
    // checkout. Only record parked items / fire the worker park after removal is
    // confirmed server-side.
    if (otherItems.length > 0) {
      let updatedCart;
      try {
        updatedCart = await this.removeItemsFromCart(otherItems);
      } catch (error) {
        console.error('BrandCartDrawer: could not isolate vendor, not redirecting', error);
        alert('Sorry, we could not prepare your single-brand checkout. Please try again.');
        return;
      }

      const stillMixed = !updatedCart
        || !Array.isArray(updatedCart.items)
        || updatedCart.items.some(item => item.vendor !== vendor);
      if (stillMixed) {
        console.error('BrandCartDrawer: cart still has other vendors after removal, not redirecting', updatedCart);
        alert('Sorry, we could not prepare your single-brand checkout. Please try again.');
        return;
      }

      // Removal confirmed → record for restore (guarded) + worker park. Uses
      // the SAME mvc_* keys as /pages/carts so one shared restore path brings
      // the items back no matter which surface parked them, and MERGES with
      // any pending un-restored record instead of overwriting it — the
      // restore tops quantities up against the live cart, so a merged record
      // can never double-add.
      try {
        let pendingParked = [];
        try {
          const prior = JSON.parse(sessionStorage.getItem('mvc_parked_items') || '[]');
          if (Array.isArray(prior)) pendingParked = prior;
        } catch (parseError) { /* corrupted prior record: start fresh */ }
        sessionStorage.setItem('mvc_checkout_vendor', vendor);
        sessionStorage.setItem('mvc_parked_items', JSON.stringify(pendingParked.concat(otherItems.map(item => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties
        })))));
      } catch (e) { /* sessionStorage blocked: restore-on-abandon degrades; checkout stays single-vendor */ }
      await this.parkItems(otherItems);
    }

    // Redirect to checkout — reached only after the cart is verified single-vendor.
    window.location.href = this.config.routes?.checkoutUrl || '/checkout';
  }

  /**
   * Park items (save for later restoration)
   */
  async parkItems(items) {
    if (!this.config.customerId || items.length === 0) {
      return; // For guests, sessionStorage is enough
    }

    try {
      await fetch(`${this.config.workerUrl}/cart/park`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: this.config.customerId,
          items: items.map(item => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties
          }))
        })
      });
    } catch (error) {
      console.error('BrandCartDrawer: Error parking items:', error);
    }
  }

  /**
   * Restore parked items when returning from a single-brand checkout. The
   * drawer renders on every storefront page, so the other brands' items come
   * back even if the buyer never revisits /pages/carts — whether checkout was
   * COMPLETED (cart now empty) or abandoned. When the /pages/carts section is
   * on the page its own script owns the restore (it reloads to re-render the
   * vendor cards), so this one defers. The record PERSISTS in sessionStorage
   * until the re-add succeeds (or is deliberately dropped), so a tab closed
   * or navigated mid-restore just retries on the next load; retries are safe
   * because the re-add tops quantities up against the live cart.
   */
  async restoreParkedItems() {
    if (document.querySelector('.multi-vendor-carts')) return;

    if (this._restoring) return;
    this._restoring = true;
    try {
      const records = [];
      let sawRecord = false;
      for (const key of ['mvc_parked_items', 'bcd_parked_items']) {
        const raw = sessionStorage.getItem(key);
        if (!raw) continue;
        sawRecord = true;
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) records.push(...parsed);
        } catch (e) { /* corrupted record: dropped below via consolidation */ }
      }
      const parkedItems = records.filter(item => item && item.id && item.quantity > 0);
      if (parkedItems.length === 0) {
        if (sawRecord) this.clearParkedRecord();
        return;
      }

      // Consolidate into the single mvc_ record so retries and the park-merge
      // in the checkout handler all operate on one source of truth.
      try { sessionStorage.setItem('mvc_parked_items', JSON.stringify(parkedItems)); } catch (e) { /* storage blocked */ }
      sessionStorage.removeItem('bcd_parked_items');
      sessionStorage.removeItem('bcd_checkout_vendor');

      // Bound the retries: a permanently failing record must not re-attempt
      // on every page load forever.
      const attempts = parseInt(sessionStorage.getItem('mvc_restore_attempts') || '0', 10) + 1;
      if (attempts > 5) {
        console.error('BrandCartDrawer: giving up on parked items after repeated failures', parkedItems);
        this.clearParkedRecord();
        return;
      }
      sessionStorage.setItem('mvc_restore_attempts', String(attempts));

      // Signal the in-flight restore so checkout handlers wait for it to
      // settle before snapshotting the cart — otherwise this /cart/add could
      // land inside another brand's checkout-redirect window and re-mix it.
      let release;
      window.__parkedRestoreInFlight = new Promise((resolve) => { release = resolve; });
      try {
        const restored = await this.readdParkedItems(parkedItems)
          .catch((error) => { console.error('BrandCartDrawer: error restoring parked items', error); return false; });
        if (restored) {
          this.clearParkedRecord();
          // Mirror the drawer's standard post-mutation broadcast so the header
          // badge and the other cart modules reflect the restored items.
          const cart = await this.fetchCartData();
          this.updateCartBubble();
          if (cart) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-drawer', cartData: cart });
            }
          }
        }
      } finally {
        release();
      }
    } finally {
      this._restoring = false;
    }
  }

  clearParkedRecord() {
    ['mvc_parked_items', 'mvc_checkout_vendor', 'mvc_checkout_all',
      'bcd_parked_items', 'bcd_checkout_vendor', 'mvc_restore_attempts']
      .forEach((key) => { try { sessionStorage.removeItem(key); } catch (e) { /* noop */ } });
  }

  /**
   * Idempotently re-add parked items: coalesce by cart line (variant plus
   * properties), top quantities up against the LIVE cart (so a retry after a
   * lost-but-applied response never double-adds), try one batch add, and on
   * failure fall back to per-item adds so one dead variant (e.g. sold out ->
   * 422) cannot hold the others hostage. Returns true when nothing
   * recoverable remains parked.
   *
   * A line is variant AND properties, never the variant alone: a stamped
   * free gift and a unit the clinic paid for can share a variant. Merged by
   * variant they came back as ONE line carrying the first record's
   * properties, so a paid unit returned stamped (and bxgy-promo.js then
   * trimmed or pruned it as a gift), or the gift lost its stamp and the
   * auto-add gave a second one.
   */
  async readdParkedItems(parkedItems) {
    const cartResponse = await brandDrawerResilientFetch(`${this.config.routes?.cartUrl || '/cart'}.js`);
    if (!cartResponse || !cartResponse.ok) return false; // cannot verify the live cart: retry next load
    const cart = await cartResponse.json();

    // Property order and a missing vs empty properties object do not make
    // two lines: Shopify keys a line on the variant and the property values.
    const lineKeyOf = (variantId, properties) => {
      const p = properties || {};
      return String(variantId) + '|' + JSON.stringify(Object.keys(p).sort().map((k) => [k, String(p[k])]));
    };

    const inCart = {};
    (cart.items || []).forEach((item) => {
      const k = lineKeyOf(item.variant_id, item.properties);
      inCart[k] = (inCart[k] || 0) + item.quantity;
    });

    const byLine = new Map();
    parkedItems.forEach((item) => {
      const k = lineKeyOf(item.id, item.properties);
      const prior = byLine.get(k);
      if (prior) prior.quantity += item.quantity;
      else byLine.set(k, { k, id: item.id, quantity: item.quantity, properties: item.properties });
    });

    const itemsToAdd = [];
    byLine.forEach((item) => {
      const needed = item.quantity - (inCart[item.k] || 0);
      if (needed > 0) itemsToAdd.push({ id: item.id, quantity: needed, properties: item.properties });
    });
    if (itemsToAdd.length === 0) return true; // everything is already in the cart

    // Single-shot plain fetch on purpose: /cart/add is not idempotent, so the
    // resilient fetch's in-flight retries could double-add. Cross-load retries
    // are safe via the top-up above.
    const post = (items) => fetch(`${this.config.routes?.cartAddUrl || '/cart/add'}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    const batch = await post(itemsToAdd);
    if (batch.ok) return true;

    // Batch adds are all-or-nothing: retry per item, drop what Shopify
    // permanently rejects, keep transient failures parked for the next try.
    const residual = [];
    for (const item of itemsToAdd) {
      try {
        const single = await post([item]);
        if (single.ok) continue;
        if (single.status === 429 || single.status >= 500) residual.push(item);
        else console.error('BrandCartDrawer: variant no longer addable, dropping', item, single.status);
      } catch (e) {
        residual.push(item); // network hiccup: retry on the next page load
      }
    }
    if (residual.length === 0) return true;
    try { sessionStorage.setItem('mvc_parked_items', JSON.stringify(residual)); } catch (e) { /* storage blocked */ }
    return false; // keep the (residual) record + attempts counter for retry
  }

  /**
   * Remove items from cart
   */
  async removeItemsFromCart(items) {
    const updates = {};
    items.forEach(item => {
      updates[item.key] = 0;
    });

    // Do NOT swallow failures: handleCheckout must know removal succeeded before it
    // redirects, otherwise a mixed-vendor cart can reach a single checkout.
    const response = await brandDrawerResilientFetch(
      `${this.config.routes?.cartUpdateUrl || '/cart/update'}.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      }
    );
    if (!response || !response.ok) {
      throw new Error(`removeItemsFromCart ${response ? response.status : 'no-response'}`);
    }
    return await response.json(); // the updated cart
  }

  /**
   * Update cart icon bubble
   */
  async updateCartBubble() {
    try {
      const response = await brandDrawerResilientFetch(`${this.config.routes?.cartUrl || '/cart'}?section_id=cart-icon-bubble`);
      if (!response || !response.ok) return;
      const html = await response.text();
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newBubble = doc.querySelector('.shopify-section');
        if (newBubble) {
          bubbleEl.innerHTML = newBubble.innerHTML;
        }
      }
    } catch (error) {
      console.error('BrandCartDrawer: Error updating cart bubble:', error);
    }
  }

  /**
   * Format money according to shop settings
   */
  formatMoney(cents) {
    const amount = cents / 100;
    // Use cart data currency (actual presentment currency) over config
    const currency = this.cartData?.currency || this.config.currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Get sized image URL
   */
  getSizedImageUrl(url, size) {
    if (!url) return '';
    return url
      .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
      .replace(/\.([^.]+)$/, `_${size}.$1`);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.brandCartDrawer = new BrandCartDrawer();
  });
} else {
  window.brandCartDrawer = new BrandCartDrawer();
}

/**
 * Global function to open the brand cart drawer
 * Can be called from anywhere: window.openBrandCartDrawer('Vendor Name', 'Product Title')
 */
window.openBrandCartDrawer = function(vendor, productTitle = null) {
  if (window.brandCartDrawer) {
    window.brandCartDrawer.openForVendor(vendor, productTitle);
  }
};

// Exported for the Node tests (same shipped-code pattern as
// bxgy-promo.js): pins the drawer-side lock decision — a line is only
// locked/mix-editable when an ACTIVE disjoint bxgy runtime claims it;
// legacy `_marini_q2_free` lines of pack or expired campaigns must stay
// normal editable lines. The class itself is exported so the tests can
// run its shipped methods (the ended gift note, the parked item restore)
// on a bare instance; in the browser `module` is undefined and nothing
// changes.
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    bxgyDisjointSlot, bxgySlots, endedGiftNoticeCopy, renderEndedGiftNotice, endedGiftsBackInCart,
    BrandCartDrawer,
  };
}
